import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, FullPageSpinner, Section, type Requirement } from "@/components/ui";
import { CustomNsNotice, RuleRow } from "@/components/domains/parts";
import { domainKeys, domainsApi, longDate, type DomainDetail, type Zone } from "@/lib/domains";

import { EmailForwardingSection } from "../forwarding/EmailForwarding";
import { UrlForwardingSection } from "../forwarding/UrlForwarding";

/**
 * Mirrors the backend's `forwarding.FORWARDING_MX_TARGET` (app/modules/domains/forwarding.py) for display
 * only: whether the zone's MX record is CortexGrip's own forwarding service, so the "Turn on/off" action
 * reflects what's actually live. A dedicated flag from the API would avoid this duplication if it grows.
 */
const FORWARDING_MX_HOST = "fwd-mx.cortexgrip.net";

/** URL and e-mail forwarding (PHP-3942, AC4-8): a new tab beside the existing Email (mailboxes) tab. */
export function ForwardingTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };
  const canManage = canOn(manage.permission, "domain_folder", detail.folder?.id);
  const custom = d.nameserver_mode === "custom";

  const zoneKey = domainKeys.zone(orgId, d.id);
  const zone = useQuery({
    queryKey: zoneKey,
    queryFn: () => domainsApi.zone(orgId, d.id),
    enabled: !custom,
  });
  const forwardingOn =
    zone.data?.records.some((r) => r.type === "MX" && r.name === "@" && r.content === FORWARDING_MX_HOST) ??
    null;

  const events = useQuery({
    queryKey: [...domainKeys.all(orgId), "events", d.id, "forwarding"],
    queryFn: () => domainsApi.events(orgId, d.id, "forwarding"),
    enabled: !custom,
  });

  const onZoneApplied = async (next: Zone) => {
    qc.setQueryData(zoneKey, next);
    await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  };

  if (custom) {
    return (
      <div className="flex flex-col gap-6">
        <CustomNsNotice />
      </div>
    );
  }
  if (zone.isPending) return <FullPageSpinner />;
  if (zone.isError) return <ErrorAlert error={zone.error} />;

  return (
    <div className="flex flex-col gap-6">
      <UrlForwardingSection domain={d} canManage={canManage} manage={manage} />
      <EmailForwardingSection
        domain={d}
        canManage={canManage}
        manage={manage}
        forwardingOn={forwardingOn}
        onZoneApplied={onZoneApplied}
      />
      <Section title="Change history">
        {events.isPending ? (
          <p className="text-[13px] text-fg-subtle">Loading…</p>
        ) : events.isError ? (
          <ErrorAlert error={events.error} />
        ) : events.data.events.length === 0 ? (
          <p className="text-[13px] text-fg-subtle">No changes yet.</p>
        ) : (
          events.data.events.map((e, i) => (
            <RuleRow key={e.id} first={i === 0}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-fg">{e.summary}</p>
                <p className="text-[11px] text-fg-subtle">
                  {longDate(e.created_at)}
                  {e.actor_email ? ` · ${e.actor_email}` : ""}
                </p>
              </div>
            </RuleRow>
          ))
        )}
      </Section>
    </div>
  );
}
